import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";
import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Transition } from "@headlessui/react";
export function TWclassNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/** Safe value for React children - objects cause "Objects are not valid as React child" */
function safeReactChild(val) {
  if (val == null) return null;
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") return val;
  if (React.isValidElement(val)) return val;
  if (Array.isArray(val)) return val.map(safeReactChild);
  return ""; // plain object - invalid
}

export const CopyToClipboard = (props) => {
  const { children, value, type, copy, content, path } = props;
  const safeChildren = safeReactChild(children);
  const [isCopied, setIsCopied] = useState(false);

  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleCopy = (value) => {
    if (!value) {
      toast.error("Cannot handleCopy an empty field");
      setIsCopied(false);
    } else {
      navigator.clipboard.writeText(value);
      setIsCopied(true);
    }
  };

  const { t } = useTranslation();

  if (type == "button") {
    return (
      <>
        {isHovering && (
          <div
            className={TWclassNames(
              isCopied ? "text-green-700" : "",
              "relative cursor-pointer flex items-center"
            )}
          >
            {safeChildren}

            <ClipboardDocumentCheckIcon
              onClick={() => handleCopy(value ?? (typeof children === "string" || typeof children === "number" ? children : null))}
              className={TWclassNames(
                isCopied ? "text-green-500" : "text-gray-400",
                "ml-0 h-5 w-5 mt-1"
              )}
              aria-hidden="true"
            />
          </div>
        )}
      </>
    );
  } else if (type == "popup") {
    return (
      <>
        <div
          className=""
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Link to={path}>{safeReactChild(content)}</Link>
          {isHovering && (
            <div
              className={TWclassNames(
                isCopied ? "text-green-700" : "",
                "z-10 absolute cursor-pointer flex items-center shadow-md bg-white rounded-md p-2"
              )}
            >
              <div
                className="flex"
                onClick={() => handleCopy(value ? value : content)}
              >
                <ClipboardDocumentCheckIcon
                  className={TWclassNames(
                    isCopied ? "text-green-500" : "text-gray-400",
                    "ml-0 h-5 w-5 mt-1"
                  )}
                  aria-hidden="true"
                />{" "}
                <span className="py-1 px-1">{t("Copy")}</span>
              </div>
            </div>
          )}
        </div>
      </>
    );
  } else {
    return (
      <div
        className={TWclassNames(
          isCopied ? "text-green-700" : "",
          "relative cursor-pointer mt-2"
        )}
        onClick={() => handleCopy(value ?? (typeof children === "string" || typeof children === "number" ? children : null))}
      >
        {safeChildren}
        <Transition
          show={isCopied}
          enter="transition-opacity duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <ClipboardDocumentCheckIcon
              className="text-green-500 h-5 w-5 "
              aria-hidden="true"
            />
          </div>
        </Transition>
      </div>
    );
  }
};

export const Popup = (props) => {
  const { children, content, textSize } = props;
  const [isHovering, setIsHovering] = useState(false);
  const anchorRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 8, width: 288 });

  useLayoutEffect(() => {
    if (!isHovering || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const width = Math.min(320, Math.max(240, window.innerWidth - 16));
    let left = r.left + r.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    let top = r.bottom + 8;
    if (top + 140 > window.innerHeight) {
      top = Math.max(8, r.top - 148);
    }
    setPos({ top, left, width });
  }, [isHovering]);

  if (!content) {
    return <>{safeReactChild(children)}</>;
  }

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex shrink-0"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onFocus={() => setIsHovering(true)}
      onBlur={() => setIsHovering(false)}
    >
      {safeReactChild(children)}
      {isHovering && typeof document !== "undefined"
        ? createPortal(
            <div
              role="tooltip"
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                width: pos.width,
                zIndex: 6000,
                whiteSpace: "normal",
                wordBreak: "normal",
                overflowWrap: "anywhere",
              }}
              className="pointer-events-none rounded-xl bg-white px-3 py-2.5 text-left shadow-lg ring-1 ring-slate-200"
            >
              <span
                className={TWclassNames(
                  textSize ? "text-" + textSize : "text-xs",
                  "block font-normal leading-5 text-slate-700"
                )}
              >
                {safeReactChild(content)}
              </span>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};
